import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState, useImperativeHandle, forwardRef, useRef } from "react";
import SmarthopFormView from "@smarthop/form/SmarthopFormView";
import { getStrategyTreeById, updateStrategy } from "app/services/strategyServices";
import { strategyEditForm } from "./config/strategyEditForm";
import { showErrorSnackbar, showSuccessSnackbar } from "app/main/utils/snackbarUtil";
import { useSnackbar } from "notistack";
import { getRoleLabel } from "app/services/LoginService";
import { incrementDataRevision } from "app/store/tools/revisionSlice";
import { replaceFormDialog } from "app/store/tools/formDialogSlice";
import { Typography } from "@material-ui/core";
import { SmarthopConfirmDialog } from "@smarthop/form";
import SmarthopDialogViewLoadingStub from "@smarthop/views/SmarthopDialogViewLoadingStub";
import { formatContractDetails } from "app/main/strategy/utils/contractUtils";

const StrategyEdit = forwardRef(({ dataIds, onDone, onLoading, onProgress, setTitle, setSize, nativeMobile }, ref) => {
	useEffect(() => {
		setTitle?.("Edit Contract");
		setSize?.("max-w-xl");
		// eslint-disable-next-line
	}, []);

	const dispatch = useDispatch();
	const snackbar = useSnackbar();
	const user = useSelector(({ auth }) => auth.user);

	const hasPermission = useMemo(() => {
		const role = getRoleLabel();
		if (role !== "driver" && role !== "carrier_manager" && role !== "carrier_generic") {
			return true;
		}
		return !!user.gatekeepers?.permission_search_on;
	}, [user]);

	const ids = useMemo(() => {
		return {
			...(dataIds ?? {}),
			carrierId: dataIds.carrierId,
			truckId: dataIds.truckId,
			strategyId: dataIds.strategyId,
		};
	}, [dataIds]);

	const [data, setData] = useState(null);
	const [formData, setFormData] = useState({});
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	const [confirmDialog, setConfirmDialog] = useState(null);

	const mergedData = useMemo(() => {
		let guarantee = data?.guarantee;
		let mergedData = {
			...(data ?? {}),

			"guarantee-total_payment": guarantee?.total_payment,
			"guarantee-total_loaded_miles": guarantee?.total_loaded_miles,
			"guarantee-total_empty_miles": guarantee?.total_empty_miles,
			"guarantee-total_rpm": guarantee?.total_rpm,
			"guarantee-total_duration": guarantee?.total_duration,
			...formData,
		};
		console.log("mergedDataModified:", mergedData);
		return mergedData;
	}, [data, formData]);

	// Create a ref to store the latest mergedData
	const latestDataRef = useRef(mergedData);

	useEffect(() => {
		latestDataRef.current = mergedData;
	}, [mergedData]);

	const handleCopy = () => {
		// Use the ref to get the latest data
		const currentData = latestDataRef.current;
		const text = formatContractDetails(currentData);
		navigator.clipboard.writeText(text).then(
			() => {
				showSuccessSnackbar(snackbar, "Contract details copied to clipboard", { variant: "success" });
			},
			() => {
				showErrorSnackbar(snackbar, "Failed to copy to clipboard", { duration: 6000 });
			}
		);
	};

	useImperativeHandle(
		ref,
		() => {
			const btns = {};

			if (ids.strategyId && !nativeMobile) {
				btns.eye = {
					onClick: () => {
						console.log("Eye button clicked - data:", data);
						viewStrategy(ids.strategyId, ids.virtualContractId, data?.carrier, data?.truck, "savedList");
					},
					icon: "remove_red_eye",
					title: "View Strategy",
				};

				btns.copy = {
					onClick: handleCopy,
					icon: "content_copy",
					title: "Copy Contract Details",
				};
			}
			return btns;
		},
		[ids, dispatch, nativeMobile, data, snackbar]
	);

	const strategyFormConfig = useMemo(() => {
		return strategyEditForm({
			ids,
			data,
			formData,
			mergedData,
		});
	}, [data, formData, ids, mergedData]);

	const strategyUpdated = () => {
		onProgress?.(true);

		// Prepare data to send
		const dataToSend = {
			status: mergedData.status,
			notes: mergedData.notes !== undefined ? mergedData.notes : "",
		};
		if (mergedData.current_location) {
			dataToSend.current_location = mergedData.current_location;
		}
		if (mergedData.available_date) {
			dataToSend.available_date = mergedData.available_date;
		}

		if (mergedData.start_date) {
			dataToSend.start_date = mergedData.start_date;
		}

		if (mergedData.end_date) {
			dataToSend.end_date = mergedData.end_date;
		}

		if (mergedData.end_location) {
			dataToSend.end_location = mergedData.end_location;
		}

		if (mergedData.start_location) {
			dataToSend.start_location = mergedData.start_location;
		}

		// Handle guarantee data if present
		if (mergedData.guarantee) {
			dataToSend.guarantee = {};

			// Only include fields that have values
			if (mergedData.guarantee.total_payment !== undefined) {
				dataToSend.guarantee.total_payment = parseFloat(mergedData.guarantee.total_payment) || 0;
			}

			if (mergedData.guarantee.total_loaded_miles !== undefined) {
				dataToSend.guarantee.total_loaded_miles = parseInt(mergedData.guarantee.total_loaded_miles) || 0;
			}

			if (mergedData.guarantee.total_empty_miles !== undefined) {
				dataToSend.guarantee.total_empty_miles = parseInt(mergedData.guarantee.total_empty_miles) || 0;
			}

			if (mergedData.guarantee.total_rpm !== undefined) {
				dataToSend.guarantee.total_rpm = parseFloat(mergedData.guarantee.total_rpm) || 0;
			}

			if (mergedData.guarantee.total_duration !== undefined) {
				dataToSend.guarantee.total_duration = parseInt(mergedData.guarantee.total_duration) || 0;
			}
		}

		console.log("Sending strategy update:", dataToSend);

		setSaving(true);
		updateStrategy(ids.strategyId, dataToSend).then(
			(response) => {
				dispatch(incrementDataRevision({ event: "profileRevision" }));
				showSuccessSnackbar(snackbar, "Contract updated successfully", { variant: "success" });
				onDone?.(response);
				setSaving(false);
				onProgress?.(false);
			},
			(error) => {
				setSaving(false);
				onProgress?.(false);
				showErrorSnackbar(snackbar, error, { duration: 6000 });
			}
		);
	};

	const viewStrategy = (strategyId, virtualContractId, carrier, truck) => {
		dispatch(
			replaceFormDialog({
				viewId: "STRATEGY_VISUALIZER",
				dataIds: {
					strategyId: strategyId,
					virtualContractId: virtualContractId,
					carrier: carrier,
					truck: truck,
					entryPoint: "savedList",
				},
			})
		);
	};

	const getStrategyData = async () => {
		setLoading(true);
		onLoading?.(true);
		try {
			const result = await getStrategyTreeById(ids.strategyId);

			// Log the structure to understand the response format
			console.log("Strategy data response:", result);

			// The API returns the strategy in result.resData.strategy
			if (result && result.resData && result.resData.strategy) {
				const truckInfo = result.resData?.truckInfo;
				const truckEmptyLocation = result.resData?.truckEmptyLocation;
				const data = { ...result.resData?.strategy, ...truckInfo, ...truckEmptyLocation };
				if (!data.start_date) {
					data.start_date = data.available_date;
				}
				if (!data.start_location) {
					data.start_location = data.current_location;
					data.start_location__view = data.current_location__view;
				}

				setData(data);
				setLoading(false);
				onLoading?.(false);
			} else {
				throw new Error("Invalid response format from strategy data");
			}
		} catch (error) {
			console.error(error);
			showErrorSnackbar(snackbar, error, { duration: 6000 });
			setLoading(false);
			onLoading?.(false);
			onDone?.();
		}
	};

	const updateFormData = (model, fieldName) => {
		const update = { [fieldName]: model[fieldName] };

		// Handle status changes that might require confirmation
		if (fieldName === "status" && update.status !== data?.status) {
			if (update.status === "CANCELED") {
				setConfirmDialog({
					flag: true,
					message: "Are you sure you want to cancel this contract?",
					field: fieldName,
					value: update.status,
				});
				return;
			}

			if (update.status === "COMPLETED") {
				setConfirmDialog({
					flag: true,
					message: "Are you sure you want to mark this contract as completed?",
					field: fieldName,
					value: update.status,
				});
				return;
			}
		}

		// Handle nested fields for guarantee data
		if (fieldName.startsWith("guarantee-")) {
			const guaranteeField = fieldName.split("-")[1];
			update.guarantee = {
				...(formData.guarantee || data?.guarantee || {}),
				[guaranteeField]: model[fieldName],
			};
			//delete update[fieldName];
		}

		setFormData((data) => ({ ...data, ...update }));
	};

	const handleConfirmDialog = (confirm) => {
		if (confirm && confirmDialog.field) {
			if (confirmDialog.field.startsWith("guarantee-")) {
				const guaranteeField = confirmDialog.field.split("-")[1];
				setFormData((data) => ({
					...data,
					guarantee: {
						...(data.guarantee || {}),
						[guaranteeField]: confirmDialog.value,
					},
				}));
			} else {
				setFormData((data) => ({ ...data, [confirmDialog.field]: confirmDialog.value }));
			}
		}
		setConfirmDialog(null);
	};

	useEffect(() => {
		if (ids.strategyId) {
			getStrategyData();
		}
		// eslint-disable-next-line
	}, [ids]);

	if (loading) {
		return <SmarthopDialogViewLoadingStub nativeMobile={nativeMobile} loading={true} />;
	}

	if (!hasPermission) {
		return (
			<SmarthopDialogViewLoadingStub
				nativeMobile={nativeMobile}
				loading={"Does not have Permission to Edit Strategy"}
			/>
		);
	}

	return (
		<>
			{data?.status === "COMPLETED" || data?.status === "CANCELED" ? (
				<div className="w-full mb-4 bg-amber-100 p-4 rounded-md">
					<Typography variant="body2" color="textSecondary">
						This strategy is {data.status.toLowerCase()}. Some fields may be read-only.
					</Typography>
				</div>
			) : null}

			<div className={"mt-6" + (saving ? " pointer-events-none opacity-60 " : "")}>
				{data && (
					<SmarthopFormView
						key={`strategy-edit-${ids.strategyId}`}
						mode={"EDIT"}
						content={strategyFormConfig}
						data={mergedData}
						noInitValidation={true}
						dataIds={ids}
						nativeMobile={nativeMobile}
						trackChangedFields={["ALL"]}
						onChangeCommitted={updateFormData}
						onSubmit={strategyUpdated}
					/>
				)}
			</div>

			<SmarthopConfirmDialog
				open={!!confirmDialog?.flag}
				title={confirmDialog?.message}
				onClose={() => handleConfirmDialog(false)}
				onAccept={() => handleConfirmDialog(true)}
				acceptMsg="Yes, Confirm"
				closeMsg="Cancel"
			/>
		</>
	);
});

export default StrategyEdit;
